// video conversion settings - optimized for file size (< 2MB target)
const GIF_MAX_WIDTH = 480; // reduced from 800 to keep file size down
const GIF_FPS = 10; // reduced from 15 to reduce file size while keeping smoothness
const GIF_MAX_DURATION = 2; // reduced from 3 seconds to 2 seconds
const GIF_COLORS = 128; // reduced color palette (128 instead of 256) for smaller file size
const GIF_MAX_SIZE_MB = 2; // target max file size in MB

interface VideoConversionResult {
  gifFile: File;
}

// convert video to animated gif (like tiktok thumbnails)
async function convertToGif(
  videoFile: File,
  onProgress?: (progress: number) => void
): Promise<File> {
  // use gif.js library - install with: bun add gif.js
  // for now, using a simple canvas-based approach

  const videoUrl = URL.createObjectURL(videoFile);
  const video = document.createElement("video");
  video.src = videoUrl;
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = "anonymous";
  video.preload = "auto";

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = reject;
    video.load();
  });

  // calculate scaled dimensions
  const aspectRatio = video.videoWidth / video.videoHeight;
  const width = Math.min(GIF_MAX_WIDTH, video.videoWidth);
  const height = Math.round(width / aspectRatio);

  // create canvas for frame extraction
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  if (!ctx) {
    URL.revokeObjectURL(videoUrl);
    throw new Error("failed to get canvas context");
  }

  // extract frames as image data
  const frames: ImageData[] = [];
  const frameInterval = 1 / GIF_FPS;
  const duration = Math.min(video.duration, GIF_MAX_DURATION); // limit duration for thumbnail
  let frameCount = 0;
  // ensure we don't exceed duration - subtract one frame interval to avoid going past the end
  const totalFrames = Math.floor((duration - 0.01) * GIF_FPS); // subtract small buffer to avoid end frame

  return new Promise((resolve, reject) => {
    let timeoutId: NodeJS.Timeout | null = null;
    let isSeeking = false;

    const extractFrame = () => {
      try {
        // check if we've reached the end (with small buffer to avoid last black frame)
        const targetTime = frameCount * frameInterval;
        if (targetTime >= duration - 0.01 || frameCount >= totalFrames) {
          video.pause();
          URL.revokeObjectURL(videoUrl);
          if (timeoutId) clearTimeout(timeoutId);

          // ensure we have at least one frame
          if (frames.length === 0) {
            reject(new Error("No frames extracted from video"));
            return;
          }

          // create animated gif from frames using gif.js
          createAnimatedGif(frames, width, height, frameInterval, onProgress)
            .then((gifFile) => resolve(gifFile))
            .catch((error) => {
              reject(
                new Error(
                  `Failed to create GIF: ${
                    error instanceof Error ? error.message : String(error)
                  }`
                )
              );
            });
          return;
        }

        // ensure video is ready and not seeking before capturing frame
        if (video.readyState >= 2 && !isSeeking) {
          // draw frame to canvas and extract image data
          ctx.drawImage(video, 0, 0, width, height);
          const imageData = ctx.getImageData(0, 0, width, height);

          // check if frame is not completely black (simple check)
          const hasContent = imageData.data.some((pixel, index) => {
            // check every 4th pixel (RGBA) - skip alpha channel
            if (index % 4 === 3) return false;
            return pixel > 10; // threshold to detect non-black pixels
          });

          // only add frame if it has content (skip black frames)
          if (hasContent || frameCount === 0) {
            frames.push(imageData);
            frameCount++;
            const progress = (frameCount / totalFrames) * 80; // extraction is 0-80%
            onProgress?.(progress);
          }

          // seek to next frame
          const nextTime = Math.min(
            targetTime + frameInterval,
            duration - 0.01
          );
          if (nextTime < duration - 0.01) {
            isSeeking = true;
            video.currentTime = nextTime;
          } else {
            // we've reached the end, finish extraction
            video.pause();
            URL.revokeObjectURL(videoUrl);
            if (timeoutId) clearTimeout(timeoutId);

            if (frames.length === 0) {
              reject(new Error("No frames extracted from video"));
              return;
            }

            createAnimatedGif(frames, width, height, frameInterval, onProgress)
              .then((gifFile) => resolve(gifFile))
              .catch((error) => {
                reject(
                  new Error(
                    `Failed to create GIF: ${
                      error instanceof Error ? error.message : String(error)
                    }`
                  )
                );
              });
          }
        }
      } catch (error) {
        video.pause();
        URL.revokeObjectURL(videoUrl);
        if (timeoutId) clearTimeout(timeoutId);
        reject(
          new Error(
            `Frame extraction error: ${
              error instanceof Error ? error.message : String(error)
            }`
          )
        );
      }
    };

    // handle seeked event
    video.onseeked = () => {
      isSeeking = false;
      extractFrame();
    };

    // add timeout to prevent hanging
    timeoutId = setTimeout(() => {
      video.pause();
      URL.revokeObjectURL(videoUrl);
      reject(
        new Error(
          "Video conversion timeout - video may be too long or corrupted"
        )
      );
    }, 30000); // 30 second timeout

    video.onerror = (error) => {
      if (timeoutId) clearTimeout(timeoutId);
      URL.revokeObjectURL(videoUrl);
      reject(new Error(`Video loading error: ${error}`));
    };

    // start extraction from the beginning
    video.currentTime = 0;
    // wait for video to be ready before starting
    video.onloadeddata = () => {
      extractFrame();
    };

    // fallback: if loadeddata doesn't fire, try after a short delay
    setTimeout(() => {
      if (frames.length === 0 && video.readyState >= 2) {
        extractFrame();
      }
    }, 100);
  });
}

// create animated gif from frames using gifenc (pure JS, no workers needed)
async function createAnimatedGif(
  frames: ImageData[],
  width: number,
  height: number,
  frameDelay: number,
  onProgress?: (progress: number) => void
): Promise<File> {
  try {
    // use gifenc library - pure JavaScript, works in browser without workers
    // @ts-ignore - gifenc types may not be available
    const gifenc = await import("gifenc");
    const { GIFEncoder, quantize, applyPalette } = gifenc;

    // create gif encoder
    const encoder = GIFEncoder({
      width,
      height,
    });

    // encode each frame with optimized settings for smaller file size
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];

      // quantize colors for GIF (reduced palette for smaller file size)
      const palette = quantize(frame.data, GIF_COLORS);
      const index = applyPalette(frame.data, palette);

      // write frame with delay
      encoder.writeFrame(index, width, height, {
        palette,
        delay: Math.round(frameDelay * 1000), // delay in milliseconds
      });

      onProgress?.(80 + (i / frames.length) * 20);
    }

    // finish encoding and get the GIF bytes
    encoder.finish();
    const gifBytes = encoder.bytes();

    // check file size and warn if too large
    const fileSizeMB = gifBytes.length / (1024 * 1024);
    if (fileSizeMB > GIF_MAX_SIZE_MB) {
      console.warn(
        `GIF size is ${fileSizeMB.toFixed(2)}MB (target: <${GIF_MAX_SIZE_MB}MB). Consider reducing resolution, FPS, or duration.`
      );
    }

    // convert to blob and then to File
    const blob = new Blob([gifBytes], { type: "image/gif" });
    const gifFile = new File([blob], "output.gif", { type: "image/gif" });

    return gifFile;
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : `Failed to create GIF: ${String(error)}`;
    console.error("createAnimatedGif error:", error);
    throw new Error(errorMessage);
  }
}

// convert video file to animated gif (like tiktok thumbnails)
export async function convertVideoInBrowser(
  videoFile: File,
  onProgress?: (progress: number) => void
): Promise<VideoConversionResult> {
  try {
    onProgress?.(0);

    // convert to gif
    const gifFile = await convertToGif(videoFile, (progress) => {
      onProgress?.(progress);
    });
    onProgress?.(100);

    return { gifFile };
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : `Video conversion failed: ${String(error)}`;
    console.error("convertVideoInBrowser error:", error);
    throw new Error(errorMessage);
  }
}
